import { useEffect, useState } from "react";
import { ActionIcon, Box, Button, createStyles, Flex, Menu, Modal, Select, Switch, Text, TextInput } from "@mantine/core";
import {
  IconArrowLeft,
  IconBell,
  IconCheck,
  IconDots,
  IconEye,
  IconFileText,
  IconMinus,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSettings,
  IconTrash,
} from "@tabler/icons-react";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import {
  createIssueCategory,
  updateIssueCategory,
  deleteIssueCategory,
  updateTemplate,
  deleteTemplate,
  getServiceRequestSettings,
  updateServiceRequestSettings,
} from "../../services/serviceRequests";
import { getAllStaff } from "../../services/staff";
import { useSrButtonClassNames, useSrFieldClassNames } from "../ServiceRequestFields";
import ServiceRequestTemplateBuilder from "../ServiceRequestTemplateBuilder";

const useStyles = createStyles(() => ({
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "var(--app-text)",
    fontSize: 13,
    fontWeight: 600,
    padding: "7px 13px",
    borderRadius: 8,
    cursor: "pointer",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    width: "fit-content",
    marginBottom: 16,
    "&:hover": { background: "var(--app-surface-2)" },
  },
  heading: { fontSize: 22, fontWeight: 700, letterSpacing: "-0.018em", color: "var(--app-text)", marginBottom: 16, "@media (max-width: 768px)": { fontSize: 19, marginBottom: 12 } },

  layout: {
    display: "grid",
    gridTemplateColumns: "220px 1fr",
    gap: 16,
    alignItems: "start",
    // A 220px side rail plus a settings panel doesn't fit a tablet, so the rail
    // turns into a tab strip above the panel (see `rail` below).
    "@media (max-width: 860px)": { gridTemplateColumns: "1fr", gap: 12 },
  },
  rail: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    // Horizontal, and scrollable rather than wrapping — three items keeps it to
    // one row on all but the narrowest phones, and a wrapped nav reads as two
    // separate groups.
    "@media (max-width: 860px)": { flexDirection: "row", gap: 6, overflowX: "auto", padding: 6 },
  },
  railItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 8,
    fontFamily: "Inter",
    fontSize: 13.5,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    textAlign: "left",
    width: "100%",
    whiteSpace: "nowrap",
    "&:hover": { background: "var(--app-surface-2)", color: "var(--app-text)" },
    "@media (max-width: 860px)": { width: "auto", flex: "none", gap: 7, padding: "9px 12px" },
  },
  railItemOn: { background: "var(--app-text)", color: "var(--app-surface)", "&:hover": { background: "var(--app-text)", color: "var(--app-surface)" } },

  panel: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    padding: 18,
    "@media (max-width: 640px)": { padding: 14 },
  },
  cardPanel: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    overflow: "hidden",
  },
  panelHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12 },
  cardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 20px",
    borderBottom: "1px solid var(--app-border)",
    "@media (max-width: 560px)": { alignItems: "stretch", flexDirection: "column", gap: 10, padding: "14px 14px" },
  },
  panelTitle: { fontSize: 15, fontWeight: 700, color: "var(--app-text)", letterSpacing: "-0.01em" },
  emptyRow: { color: "var(--app-text-muted)", fontSize: 13, padding: "16px 20px" },

  addBtn: {
    height: 36,
    padding: "0 14px",
    borderRadius: 8,
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    fontSize: 13,
    fontWeight: 700,
    "&:hover": { backgroundColor: "var(--app-primary) !important", opacity: 0.85 },
  },
  darkBtn: {
    height: 36,
    padding: "0 14px",
    borderRadius: 8,
    backgroundColor: "var(--app-text)",
    color: "var(--app-surface)",
    fontSize: 13,
    fontWeight: 700,
    "&:hover": { backgroundColor: "var(--app-text) !important", opacity: 0.85 },
  },

  // Name + tags, then Active/Inactive, Approvals and the kebab. On a phone the
  // name takes its own line and the three controls sit under it on a second.
  row: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "14px 16px",
    borderTop: "1px solid var(--app-border-soft, var(--app-border))",
    "&:first-of-type": { borderTop: "none" },
    "@media (max-width: 640px)": { flexWrap: "wrap", gap: 10, padding: "12px 14px" },
  },
  rowInfo: { flex: 1, minWidth: 0, "@media (max-width: 640px)": { flexBasis: "100%" } },
  rowName: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13.5,
    fontWeight: 700,
    color: "var(--app-text)",
    letterSpacing: "-0.005em",
    whiteSpace: "nowrap",
    // A long template name has nowhere to go on a phone but a second line.
    "@media (max-width: 640px)": { whiteSpace: "normal" },
  },
  trowTags: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5 },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 6,
    padding: "2px 8px",
    whiteSpace: "nowrap",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    borderRadius: 999,
    padding: "4px 12px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".05em",
    fontFamily: "Inter",
    border: "1px solid transparent",
    cursor: "pointer",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  statusActive: { background: "var(--app-surface-2)", color: "var(--app-text)", borderColor: "var(--app-border)" },
  statusInactive: { background: "var(--app-surface-2)", color: "var(--app-text-muted)", borderColor: "var(--app-border)" },
  kebabBtn: {
    width: 30,
    height: 30,
    borderRadius: 7,
    color: "var(--app-text-muted)",
    "&:hover": { background: "var(--app-surface-2)", color: "var(--app-text)" },
  },

  // Mockup's .wiz-section is `padding: 20px 0` on every section, not just
  // paddingTop — without a section's own bottom padding, its divider sits
  // flush against the previous section's last control with no gap above it.
  section: {
    paddingTop: 20,
    paddingBottom: 20,
    borderTop: "1px solid var(--app-border)",
    "&:first-of-type": { paddingTop: 0, borderTop: "none" },
  },
  sectionHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 },
  sectionLabel: { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--app-text-muted)" },
  sectionHint: { fontSize: 12, color: "var(--app-text-muted)" },

  toggleRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, maxWidth: 560, marginBottom: 14,
    // The switch stays on the label's row (it's the whole point of the row), so
    // only the gap tightens — the description wraps underneath as it already does.
    "@media (max-width: 560px)": { gap: 10 },
  },
  toggleTitle: { fontSize: 14, fontWeight: 600, color: "var(--app-text)" },
  toggleSub: { fontSize: 12, color: "var(--app-text-muted)", marginTop: 1 },
  remindLabel: { textTransform: "uppercase", fontWeight: 700, letterSpacing: ".04em", fontSize: 11, marginBottom: 8 },

  footRow: {
    display: "flex", justifyContent: "flex-end", borderTop: "1px solid var(--app-border)", paddingTop: 16, marginTop: 18,
    // "Save Notification Settings" is a wide label — it gets the row on a phone
    // rather than being clipped against the right edge.
    "@media (max-width: 480px)": { "& > button": { width: "100%" } },
  },

  catList: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    overflow: "hidden",
  },
  catRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "11px 14px",
    borderTop: "1px solid var(--app-border)",
    "&:first-of-type": { borderTop: "none" },
  },
  catRowR: { display: "flex", alignItems: "center", gap: 12 },
  catState: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: "var(--app-text-muted)",
    minWidth: 56,
    textAlign: "right",
  },
  catDelBtn: {
    width: 26,
    height: 26,
    borderRadius: 6,
    fontSize: 13,
    color: "var(--app-text-muted)",
    "&:hover": { background: "#FEF2F2", color: "var(--app-danger, #DC2626)" },
  },
  catName: { fontSize: 13.5, fontWeight: 600, fontFamily: "Inter" },
  // "Add a category…" + the Add button: the input needs the full width on a
  // phone, so the button drops under it.
  addRow: { display: "flex", gap: 8, marginTop: 12, "@media (max-width: 480px)": { flexDirection: "column" } },
  addRowInput: { flex: 1 },

  stepper: { display: "inline-flex", alignItems: "center", gap: 10 },
  flexField: { flex: 1 },
  stepBtn: { width: 34, height: 34, borderRadius: 8, borderColor: "var(--app-border)" },
  stepValue: { minWidth: 24, textAlign: "center" },

  // ── Approvals modal (mirrors the mockup's .m-head / .apv-* blocks) ──
  apvHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "18px 22px 6px",
    "@media (max-width: 560px)": { padding: "16px 16px 6px" },
  },
  apvTitle: { fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em", color: "var(--app-text)" },
  apvEditBtn: {
    border: 0,
    background: "none",
    padding: "2px 4px",
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    color: "#B45309",
    fontFamily: "Inter",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
  apvBody: { padding: "14px 22px 6px", display: "grid", gap: 12, "@media (max-width: 560px)": { padding: "12px 16px 6px" } },
  apvSection: { border: "1px solid var(--app-border)", borderRadius: 12, overflow: "hidden" },
  apvSectionHead: {
    padding: "12px 16px",
    borderBottom: "1px solid var(--app-border)",
    background: "var(--app-surface-2)",
    fontSize: 14,
    fontWeight: 700,
    color: "var(--app-text)",
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  apvEyebrow: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--app-text-muted)" },
  apvSectionBody: { padding: 16, "@media (max-width: 560px)": { padding: 13 } },
  apvLabel: {
    display: "block",
    fontSize: 11.5,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    textTransform: "uppercase",
    letterSpacing: ".05em",
    marginBottom: 8,
  },
  apvQuorumView: { fontSize: 14, color: "var(--app-text)" },
  apvStepN: { minWidth: 40, textAlign: "center", fontSize: 15, fontWeight: 700 },
  apvStepHint: { marginLeft: 8, fontSize: 12.5, color: "var(--app-text-muted)" },
  apvList: { display: "flex", flexDirection: "column", border: "1px solid var(--app-border)", borderRadius: 10, overflow: "hidden" },
  apvPick: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "11px 14px",
    border: 0,
    borderTop: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    fontFamily: "Inter",
    textAlign: "left",
    cursor: "pointer",
    color: "var(--app-text-muted)",
    "&:first-of-type": { borderTop: 0 },
    "&:hover": { background: "var(--app-surface-2)" },
  },
  apvPickOn: { color: "var(--app-text)" },
  apvViewRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "11px 14px",
    borderTop: "1px solid var(--app-border)",
    color: "var(--app-text)",
    "&:first-of-type": { borderTop: 0 },
  },
  apvName: { fontSize: 13.5, fontWeight: 600 },
  apvRole: { fontSize: 11, fontWeight: 600, color: "var(--app-text-muted)", marginLeft: 4 },
  apvAdd: { fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--app-text-muted)" },
  apvEmpty: { padding: "11px 14px", fontSize: 13, color: "var(--app-text-muted)" },
  apvHint: { fontSize: 11.5, color: "var(--app-text-muted)", lineHeight: 1.5 },
  apvFoot: {
    padding: "16px 22px", display: "flex", justifyContent: "flex-end", gap: 8,
    "@media (max-width: 560px)": { padding: "14px 16px", "& > button": { flex: 1 } },
  },
}));

const ToggleRow = ({ title, sub, checked, onChange }) => {
  const { classes } = useStyles();
  return (
    <Box className={classes.toggleRow}>
      <Box>
        <Text className={classes.toggleTitle}>{title}</Text>
        <Text className={classes.toggleSub}>{sub}</Text>
      </Box>
      <Switch checked={checked} onChange={(e) => onChange(e.currentTarget.checked)} color="yellow" />
    </Box>
  );
};

// Embedded as the "Issue Categories" section of the General panel, matching
// the mockup's renderGeneral() layout (not its own top-level tab).
const IssueCategoriesSection = () => {
  const { classes } = useStyles();
  const fieldClassNames = useSrFieldClassNames();
  const { issueCategories } = useSelector((state) => state.serviceRequests);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const addCategory = () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    createIssueCategory({ name }, () => {
      setSaving(false);
      setNewName("");
    });
  };

  const toggleActive = (cat) => updateIssueCategory(cat.id, { active: !cat.active });

  const removeCategory = async (cat) => {
    const willDelete = await swal({
      title: "Delete this category?",
      text: `"${cat.name}" — requests already using it are unaffected unless the backend reports it's still in use.`,
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) deleteIssueCategory(cat.id);
  };

  return (
    <Box className={classes.section}>
      <Box className={classes.sectionHead}>
        <Text className={classes.sectionLabel}>Issue Categories</Text>
      </Box>
      {!issueCategories?.length && <Text c="var(--app-text-muted)" fz={13} mb={12}>No issue categories yet.</Text>}
      {!!issueCategories?.length && (
        <Box className={classes.catList}>
          {issueCategories.map((cat) => (
            <Box key={cat.id} className={classes.catRow}>
              <Text className={classes.catName} c={cat.active ? "var(--app-text)" : "var(--app-text-muted)"}>
                {cat.name}
                {cat.isDefault && <span className={classes.chip} style={{ marginLeft: 8 }}>Default</span>}
              </Text>
              {/* Global defaults are system-wide: viewable, always active, but
                  never editable/deletable per-org — same rule as default
                  templates (no toggle, no delete). */}
              {cat.isDefault ? (
                <Box className={classes.catRowR}>
                  <Text className={classes.catState}>Active</Text>
                </Box>
              ) : (
                <Box className={classes.catRowR}>
                  <Text className={classes.catState}>{cat.active ? "Active" : "Inactive"}</Text>
                  <Switch checked={cat.active} onChange={() => toggleActive(cat)} color="yellow" />
                    {!cat?.General ? 
                    
                  <>
                     <ActionIcon variant="subtle" className={classes.catDelBtn} onClick={() => removeCategory(cat)} aria-label="Remove category">
                    ✕
                  </ActionIcon>
                  
                  </>:null
                  }
               
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
      <Box className={classes.addRow}>
        <TextInput
          placeholder="Add a category…"
          value={newName}
          onChange={(e) => setNewName(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && addCategory()}
          classNames={fieldClassNames}
          className={classes.addRowInput}
        />
        <Button variant="filled" loading={saving} onClick={addCategory} className={classes.addBtn}>
          Add Category
        </Button>
      </Box>
    </Box>
  );
};

// Request Number Prefix / Allow-requesters-to-cancel aren't exposed by the
// API yet (no endpoint documents them) — kept as local-only state so the
// section still looks and behaves like the mockup; only Issue Categories
// below it is wired to the real API.
const GeneralPanel = () => {
  const { classes } = useStyles();
  const fieldClassNames = useSrFieldClassNames();
  const { settings, settingsSaving } = useSelector((state) => state.serviceRequests);
  const [prefix, setPrefix] = useState(settings.requestNumberPrefix);
  const [allowCancel, setAllowCancel] = useState(settings.allowRequesterCancel);

  useEffect(() => {
    setPrefix(settings.requestNumberPrefix);
    setAllowCancel(settings.allowRequesterCancel);
  }, [settings.requestNumberPrefix, settings.allowRequesterCancel]);

  const save = () => {
    updateServiceRequestSettings(
      { requestNumberPrefix: prefix, allowRequesterCancel: allowCancel },
      () => swal({ title: "Saved", icon: "success" })
    );
  };

  return (
    <Box className={classes.panel}>
      <Box className={classes.panelHead}>
        <Text className={classes.panelTitle}>General</Text>
      </Box>

      <Box className={classes.section}>
        <Box className={classes.sectionHead}><Text className={classes.sectionLabel}>Request Numbering</Text></Box>
        <Flex gap={16} wrap="wrap" mb={4}>
          <TextInput
            label="Request Number Prefix"
            maxLength={4}
            value={prefix}
            onChange={(e) => setPrefix(e.currentTarget.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4) || "SR")}
            classNames={fieldClassNames}
            miw={180}
            className={classes.flexField}
          />
          <TextInput label="Next Request Number" value={settings.nextRequestNumber} disabled classNames={fieldClassNames} miw={180} className={classes.flexField} />
        </Flex>
      </Box>

      <Box className={classes.section}>
        <Box className={classes.sectionHead}><Text className={classes.sectionLabel}>Requester Permissions</Text></Box>
        <ToggleRow
          title="Allow requesters to cancel"
          sub="Requesters can cancel their own requests while still pending"
          checked={allowCancel}
          onChange={setAllowCancel}
        />
      </Box>

      <IssueCategoriesSection />

      <Box className={classes.footRow}>
        <Button variant="filled" onClick={save} loading={settingsSaving} className={classes.darkBtn}>Save General Settings</Button>
      </Box>
    </Box>
  );
};

// View mode is read-only; the Edit action gates add/remove of approvers and
// quorum changes (ticket requirement) — Cancel discards in-progress edits
// back to the template's last-saved values rather than closing the modal.
// readOnly drops the Edit affordance entirely (global default templates).
const ApprovalsModal = ({ template, close, readOnly }) => {
  const { classes, cx } = useStyles();
  const btnClass = useSrButtonClassNames();
  const { allStaff } = useSelector((state) => state.staff);
  const { profile } = useSelector((state) => state.profile);
  const currentUserId = profile?.user?.id || profile?.user?._id;
  const savedApprovers = (template?.approvers || []).map(String);
  const [editing, setEditing] = useState(false);
  const [approvers, setApprovers] = useState(savedApprovers);
  const [quorum, setQuorum] = useState(template?.defaultQuorumRequired || 1);
  const [saving, setSaving] = useState(false);

  // Same admin roster + fetch pattern as ServiceRequestTemplateBuilder's
  // approver picker — Ogiso-platform admins only, excluding inactive/deleted.
  // The tenant's own Administrator account is a User, not a staff record, so
  // it never appears in allStaff — merge it in from the profile (falling
  // back to it when it's missing from the staff list) or it shows "Unknown"
  // whenever it's already a saved approver.
  useEffect(() => {
    getAllStaff("all", false);
  }, []);
  const admins = (() => {
    const list = (allStaff || []).filter(
      (s) => s?.active !== false && s?.deleted !== true && s?.role === "admin" && s?.platform === "ogiso"
    );
    if (!currentUserId) return list;
    const me = list.find((a) => String(a.id || a._id) === String(currentUserId)) || profile?.user || null;
    const rest = list.filter((a) => String(a.id || a._id) !== String(currentUserId));
    return me ? [me, ...rest] : list;
  })();
  const idOf = (a) => String(a.id || a._id);
  const adminOf = (id) => admins.find((a) => idOf(a) === id);
  const displayName = (a) => a?.name || [a?.firstName, a?.lastName].filter(Boolean).join(" ") || "Unnamed";
  const nameOfApprover = (id) => {
    const a = adminOf(id);
    return a ? displayName(a) : "Unknown";
  };
  const roleOfApprover = (id) => {
    if (String(id) === String(currentUserId)) return "Owner";
    const role = adminOf(id)?.role;
    return role ? role.charAt(0).toUpperCase() + role.slice(1) : "";
  };

  const startEdit = () => {
    setApprovers(savedApprovers);
    setQuorum(template?.defaultQuorumRequired || 1);
    setEditing(true);
  };

  // Unlike the mockup (which mutates in place), toggling here only stages the
  // change — Done is what persists it, so the quorum is clamped alongside.
  const toggleApprover = (id) => {
    const next = approvers.includes(id) ? approvers.filter((x) => x !== id) : [...approvers, id];
    setApprovers(next);
    if (next.length) setQuorum((q) => Math.min(Math.max(q, 1), next.length));
  };

  const save = () => {
    setSaving(true);
    updateTemplate(template.id, { approvers, defaultQuorumRequired: quorum }, () => {
      setSaving(false);
      setEditing(false);
      close();
    });
  };

  const shownApprovers = editing ? approvers : savedApprovers;
  const shownQuorum = editing ? quorum : template?.defaultQuorumRequired || 1;
  const plural = (n) => `approver${n === 1 ? "" : "s"}`;

  return (
    <Modal opened onClose={close} withCloseButton={false} centered radius={16} padding={0} size={440} zIndex={1000}>
      <Box className={classes.apvHead}>
        <Text className={classes.apvTitle}>Approvals</Text>
        {!editing && !readOnly && (
          <button type="button" className={classes.apvEditBtn} onClick={startEdit}>
            <IconPencil size={13} />Edit
          </button>
        )}
      </Box>

      <Box className={classes.apvBody}>
        <Box className={classes.apvSection}>
          <Box className={classes.apvSectionHead}>
            <span className={classes.apvEyebrow}>Request Template</span>
            {template?.name}
          </Box>
          <Box className={classes.apvSectionBody}>
            <Box mb={18}>
              <Text component="label" className={classes.apvLabel}>Approvals Required</Text>
              {editing ? (
                <Flex align="center" gap={4}>
                  <ActionIcon variant="default" className={classes.stepBtn} onClick={() => setQuorum((n) => Math.max(1, n - 1))}>
                    <IconMinus size={14} />
                  </ActionIcon>
                  <Text className={classes.apvStepN}>{quorum}</Text>
                  <ActionIcon
                    variant="default"
                    className={classes.stepBtn}
                    disabled={approvers.length > 0 && quorum >= approvers.length}
                    onClick={() => setQuorum((n) => (approvers.length > 0 ? Math.min(approvers.length, n + 1) : n + 1))}
                  >
                    <IconPlus size={14} />
                  </ActionIcon>
                  <Text className={classes.apvStepHint}>
                    {approvers.length ? `of ${approvers.length} ${plural(approvers.length)}` : "any admin may approve"}
                  </Text>
                </Flex>
              ) : (
                <Text className={classes.apvQuorumView}>
                  <strong>{shownQuorum}</strong>
                  {shownApprovers.length ? ` of ${shownApprovers.length} ${plural(shownApprovers.length)} required` : " approval required — any admin may approve"}
                </Text>
              )}
            </Box>

            <Box>
              <Text component="label" className={classes.apvLabel}>Who Can Approve</Text>
              <Box className={classes.apvList}>
                {editing
                  ? admins.map((a) => {
                      const id = idOf(a);
                      const on = approvers.includes(id);
                      return (
                        <button
                          key={id}
                          type="button"
                          className={cx(classes.apvPick, on && classes.apvPickOn)}
                          onClick={() => toggleApprover(id)}
                        >
                          <span className={classes.apvName}>
                            {displayName(a)}
                            {roleOfApprover(id) && <span className={classes.apvRole}>{roleOfApprover(id)}</span>}
                          </span>
                          {on ? <IconCheck size={15} stroke={2.5} /> : <span className={classes.apvAdd}>+ Add</span>}
                        </button>
                      );
                    })
                  : shownApprovers.map((id) => (
                      <Box key={id} className={classes.apvViewRow}>
                        <span className={classes.apvName}>
                          {nameOfApprover(id)}
                          {roleOfApprover(id) && <span className={classes.apvRole}>{roleOfApprover(id)}</span>}
                        </span>
                        <IconCheck size={15} stroke={2.5} />
                      </Box>
                    ))}
                {!editing && !shownApprovers.length && (
                  <Text className={classes.apvEmpty}>Any admin may approve — not yet limited to specific people</Text>
                )}
                {editing && !admins.length && <Text className={classes.apvEmpty}>No admins available.</Text>}
              </Box>
            </Box>
          </Box>
        </Box>

        {readOnly && (
          <Text className={classes.apvHint}>
            This is a global default template — its approvals are managed system-wide and can&apos;t be edited here.
          </Text>
        )}
      </Box>

      <Box className={classes.apvFoot}>
        {editing ? (
          <Button variant="filled" loading={saving} onClick={save} className={classes.addBtn}>Done</Button>
        ) : (
          <Button variant="default" className={btnClass} onClick={close}>Close</Button>
        )}
      </Box>
    </Modal>
  );
};

const TemplatesPanel = ({ onOpenBuilder }) => {
  const { classes, cx } = useStyles();
  const btnClass = useSrButtonClassNames();
  const { templates } = useSelector((state) => state.serviceRequests);
  const [approvalsFor, setApprovalsFor] = useState(null);

  const toggleActive = (t) => updateTemplate(t.id, { active: !t.active });

  const removeTemplate = async (t) => {
    const willDelete = await swal({
      title: "Delete this template?",
      text: `"${t.name}" — blocked if any open request still uses it.`,
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) deleteTemplate(t.id);
  };

  return (
    <Box className={classes.cardPanel}>
      {approvalsFor && (
        <ApprovalsModal template={approvalsFor} readOnly={!!approvalsFor.isDefault} close={() => setApprovalsFor(null)} />
      )}
      <Box className={classes.cardHead}>
        <Text className={classes.panelTitle}>Request Templates</Text>
        <Button variant="filled" leftIcon={<IconPlus size={16} stroke={2.5} />} onClick={() => onOpenBuilder(null)} className={classes.addBtn}>
          New Template
        </Button>
      </Box>
      {!templates?.length && <Text className={classes.emptyRow}>No templates yet.</Text>}
      {templates?.map((t) => (
        <Box key={t.id} className={classes.row}>
          <Box className={classes.rowInfo}>
            <Text className={classes.rowName}>{t.name}</Text>
            <Box className={classes.trowTags}>
              {t.isDefault && <span className={classes.chip}>Default</span>}
              <span className={classes.chip}>{t.fields?.length ?? 0} field{t.fields?.length === 1 ? "" : "s"}</span>
              <span className={classes.chip}>
                {t.approvers?.length ? `${t.defaultQuorumRequired ?? 1}/${t.approvers.length} approvals` : `${t.defaultQuorumRequired ?? 1} approval${(t.defaultQuorumRequired ?? 1) === 1 ? "" : "s"} required`}
              </span>
            </Box>
          </Box>
          {/* Every template's status is togglable, including a global default —
              a default's toggle is scoped to this org alone (the shared doc is
              never touched; see the backend's inactiveAdmins opt-out list), so
              turning one off hides it from this admin and their staff only.
              Its definition still isn't editable/deletable per-org: approvals
              stay read-only and the kebab offers View only. */}
          <button
            type="button"
            className={cx(classes.statusBadge, t.active ? classes.statusActive : classes.statusInactive)}
            onClick={() => toggleActive(t)}
            title={t.isDefault
              ? `${t.active ? "Deactivate" : "Activate"} this default template for your organization`
              : `${t.active ? "Deactivate" : "Activate"} this template`}
          >
            {t.active ? "Active" : "Inactive"}
          </button>
          <Button variant="default" size="xs" className={btnClass} leftIcon={<IconRefresh size={14} />} onClick={() => setApprovalsFor(t)}>
            Approvals
          </Button>
          <Menu shadow="md" width={170} withinPortal position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" className={classes.kebabBtn}><IconDots size={16} /></ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item icon={<IconEye size={15} />} onClick={() => onOpenBuilder(t, true)}>View</Menu.Item>
              {!t.isDefault && (
                <>
                  <Menu.Item icon={<IconPencil size={15} />} onClick={() => onOpenBuilder(t)}>Edit</Menu.Item>
                  <Menu.Divider />
                  <Menu.Item color="red" icon={<IconTrash size={15} />} onClick={() => removeTemplate(t)}>Delete</Menu.Item>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        </Box>
      ))}
    </Box>
  );
};

const DIGEST_TIMES = ["7:00 AM", "8:00 AM", "9:00 AM", "12:00 PM", "5:00 PM"];

const NotificationsPanel = () => {
  const { classes } = useStyles();
  const fieldClassNames = useSrFieldClassNames();
  const { settings, settingsSaving } = useSelector((state) => state.serviceRequests);
  const [emailNewRequest, setEmailNewRequest] = useState(settings.emailNewRequest);
  const [emailDecision, setEmailDecision] = useState(settings.emailDecision);
  const [emailResolved, setEmailResolved] = useState(settings.emailResolved);
  const [dailyDigest, setDailyDigest] = useState(settings.dailyDigest);
  const [digestTime, setDigestTime] = useState(settings.digestTime);
  const [escalate, setEscalate] = useState(settings.escalate);
  const [escalateDays, setEscalateDays] = useState(settings.escalateDays);

  useEffect(() => {
    setEmailNewRequest(settings.emailNewRequest);
    setEmailDecision(settings.emailDecision);
    setEmailResolved(settings.emailResolved);
    setDailyDigest(settings.dailyDigest);
    setDigestTime(settings.digestTime);
    setEscalate(settings.escalate);
    setEscalateDays(settings.escalateDays);
  }, [settings]);

  const save = () => {
    updateServiceRequestSettings(
      { emailNewRequest, emailDecision, emailResolved, dailyDigest, digestTime, escalate, escalateDays },
      () => swal({ title: "Saved", icon: "success" })
    );
  };

  return (
    <Box className={classes.panel}>
      <Box className={classes.panelHead}>
        <Text className={classes.panelTitle}>Notifications</Text>
      </Box>

      <Box className={classes.section}>
        <Box className={classes.sectionHead}>
          <Text className={classes.sectionLabel}>Emails</Text>
          <Text className={classes.sectionHint}>In-app notifications are always on</Text>
        </Box>
        <ToggleRow title="New request submitted" sub="Email the notified admins when a request is created" checked={emailNewRequest} onChange={setEmailNewRequest} />
        <ToggleRow title="Decision made" sub="Email the requester when their request is approved or rejected" checked={emailDecision} onChange={setEmailDecision} />
        <ToggleRow title="Request resolved" sub="Email the requester when fulfillment is completed" checked={emailResolved} onChange={setEmailResolved} />
      </Box>

      <Box className={classes.section}>
        <Box className={classes.sectionHead}><Text className={classes.sectionLabel}>Digest & Reminders</Text></Box>
        <ToggleRow title="Daily digest" sub="One morning email summarizing all open requests per approver" checked={dailyDigest} onChange={setDailyDigest} />
        {dailyDigest && (
          <Select label="Digest Time" data={DIGEST_TIMES} value={digestTime} onChange={setDigestTime} classNames={fieldClassNames} mb={14} maw={220} />
        )}
        <ToggleRow title="Escalation reminder" sub="Remind approvers when a request sits unviewed" checked={escalate} onChange={setEscalate} />
        {escalate && (
          <Box mb={4}>
            <Text className={classes.remindLabel}>Remind After</Text>
            <Flex align="center" gap={12}>
              <span className={classes.stepper}>
                <ActionIcon variant="default" className={classes.stepBtn} onClick={() => setEscalateDays((n) => Math.max(1, n - 1))}><IconMinus size={13} /></ActionIcon>
                <Text fw={700}>{escalateDays}</Text>
                <ActionIcon variant="default" className={classes.stepBtn} onClick={() => setEscalateDays((n) => n + 1)}><IconPlus size={13} /></ActionIcon>
              </span>
              <Text fz={12.5} c="var(--app-text-muted)">day{escalateDays === 1 ? "" : "s"} without a view</Text>
            </Flex>
          </Box>
        )}
      </Box>

      <Box className={classes.footRow}>
        <Button variant="filled" onClick={save} loading={settingsSaving} className={classes.darkBtn}>Save Notification Settings</Button>
      </Box>
    </Box>
  );
};

const NAV = [
  { key: "templates", label: "Request Templates", icon: IconFileText },
  { key: "general", label: "General", icon: IconSettings },
  { key: "notifications", label: "Notifications", icon: IconBell },
];

const ServiceRequestSettings = ({ onBack }) => {
  const { classes, cx } = useStyles();
  const [tab, setTab] = useState("templates");
  const [builderTemplate, setBuilderTemplate] = useState(undefined); // undefined = closed, null = new, object = edit
  const [builderView, setBuilderView] = useState(false); // true = opened from the row's View action (read-only)

  useEffect(() => {
    getServiceRequestSettings();
  }, []);

  const openBuilder = (template, view = false) => {
    setBuilderView(view);
    setBuilderTemplate(template);
  };
  const closeBuilder = () => {
    setBuilderTemplate(undefined);
    setBuilderView(false);
  };

  if (builderTemplate !== undefined) {
    return <ServiceRequestTemplateBuilder template={builderTemplate} viewOnly={builderView} close={closeBuilder} />;
  }

  return (
    <Box>
      <button type="button" className={classes.backLink} onClick={onBack}>
        <IconArrowLeft size={14} />Back
      </button>
      <Text className={classes.heading}>Settings</Text>
      <Box className={classes.layout}>
        <Box className={classes.rail}>
          {NAV.map(({ key, label, icon: Icon }) => (
            <button key={key} type="button" className={cx(classes.railItem, tab === key && classes.railItemOn)} onClick={() => setTab(key)}>
              <Icon size={16} />
              {label}
            </button>
          ))}
        </Box>
        <Box>
          {tab === "templates" && <TemplatesPanel onOpenBuilder={openBuilder} />}
          {tab === "general" && <GeneralPanel />}
          {tab === "notifications" && <NotificationsPanel />}
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceRequestSettings;
